/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  combine,
  onConnect,
  reaction,
  sleep,
  withConcurrency,
} from '@reatom/framework'

import { Result, type TypeHelper } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms'
import { reatomRequest } from '@root/shared/lib'

import * as CoinsLaunchedTime from '../model/coins-launched-time'
import * as CoinsType from '../model/coins-type'

import { fetchMyCoinsRequestAction } from './fetch-coins-request-action'

import type { FetchMyCoinsArgs } from './fetch-coins-request-action'
import type { DelugeId } from '@root/entities/user-short'

export const reatomCoinsFilter = (options: { delugeId: DelugeId }) => {
  const coinsTypeAtom = atom<CoinsType.CoinsType>(
    CoinsType.Created,
    'coinsTypeAtom',
  )

  const launchedTimeAtom = atom<CoinsLaunchedTime.CoinsLaunchedTime>(
    CoinsLaunchedTime.allTimes[0],
    'launchedTimeAtom',
  )

  const filterStateAtom = combine(
    {
      coinsType: coinsTypeAtom,
      launchedTime: launchedTimeAtom,
      selectedNetwork: selectedNetworkAtom,
    },
    'filterStateAtom',
  )

  const fetchData = action(
    async (ctx, params: TypeHelper.OmitKey<FetchMyCoinsArgs, 'delugeId'>) => {
      const result = await ctx.schedule(() =>
        fetchMyCoinsRequestAction(ctx, {
          ...params,
          delugeId: options.delugeId,
        }),
      )

      return result.pipe(
        Result.map((it) =>
          it.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
      )
    },
  )

  const filterItemsStateAtom = reatomRequest({
    name: 'filterItemsState',
    execAction: fetchData,
  })

  const filterStateChangeReaction = reaction(async (ctx) => {
    const filterState = ctx.spy(filterStateAtom)

    await ctx.schedule(() => sleep(300))

    await ctx.schedule(() => filterItemsStateAtom.execute(ctx, filterState))
  }, 'filterStateChangeReaction').pipe(withConcurrency())

  onConnect(filterItemsStateAtom, (ctx) => {
    const filterStateReactionResult = filterStateChangeReaction(ctx)

    return () => {
      filterStateReactionResult.unsubscribe()
    }
  })

  return {
    filterItemsStateAtom,
    coinsTypeAtom,
    launchedTimeAtom,
    launchedTimes: CoinsLaunchedTime.allTimes,
  }
}
